import React from 'react';
import moment from 'moment';
import DraftStorage from './DraftStorage';
import { throttle } from 'lodash';
import type { DocumentValue, DocumentValueV1 } from '@aha-app/aha-editor-v2';

export type DraftValue = DocumentValue | DocumentValueV1;
interface DraftManagerProps {
  value: DraftValue;
  active: boolean;
  saveValueChanges: boolean;
  keyPrefix: string;
  keyValue?: string;
  isEmptyValue: (value: DraftValue) => boolean;
  documentChanged: (previousValue: DraftValue, newValue: DraftValue) => boolean;
  onRestoreDraft: (value: DraftValue) => void;
  onUndoDraft: (value: DraftValue) => void;
}

export default class DraftManager extends React.Component<DraftManagerProps> {
  initialValue: DraftValue;
  hideDraftTimer: ReturnType<typeof setTimeout> | undefined;
  draftStorage: DraftStorage;
  throttledSaveDraft: ((value: DraftValue) => void) & { cancel: () => void };
  enclosingForm: HTMLFormElement | null;
  ref: React.LegacyRef<HTMLDivElement>;

  state = {
    showRestoreText: false,
  };

  constructor(props) {
    super(props);
    const { value } = props;
    this.initialValue = value;
    this.hideDraftTimer = undefined;
    this.ref = React.createRef();
  }

  undoDraftRestore = event => {
    event.preventDefault();
    this.setState({ showRestoreText: false });
    this.draftStorage.deleteDraft();
    this.props.onUndoDraft(this.initialValue);
  };

  draftOrValue(value) {
    if (this.props.active) {
      const { draft } = this.draftStorage;
      if (draft && !this.props.isEmptyValue(draft)) {
        this.setState({ showRestoreText: true });
        clearTimeout(this.hideDraftTimer);
        this.hideDraftTimer = setTimeout(() => {
          this.setState({ showRestoreText: false });
        }, 10000);
        return draft;
      }
    }
    return value;
  }

  deleteCurrentDraft() {
    this.draftStorage.deleteDraft();
    if (this.throttledSaveDraft) {
      this.throttledSaveDraft.cancel();
    }
  }

  componentDidMount() {
    if (this.props.active) {
      const domNode = this.ref;
      if (typeof domNode === 'string' || typeof domNode === 'function') return;
      if (!domNode || !domNode.current) return;

      this.enclosingForm = domNode.current.closest('form');
      this.draftStorage = new DraftStorage({
        enclosingForm: this.enclosingForm,
        keyPrefix: this.props.keyPrefix,
        keyValue: this.props.keyValue,
        isEmptyValue: this.props.isEmptyValue,
      });

      this.throttledSaveDraft = throttle(
        this.draftStorage.saveDraft.bind(this.draftStorage),
        500
      );

      this.deleteCurrentDraft = this.deleteCurrentDraft.bind(this);

      // Wait until slate is initialized to restore a draft, because
      // the value of slate initializing itself is clobbering the value
      // that is trying to be restored from a draft. Waiting long enough
      // to allows the draft to be restored properly.
      window.setTimeout(() => {
        this.props.onRestoreDraft(this.draftOrValue(this.initialValue));
      }, 100);

      if (this.enclosingForm) {
        this.enclosingForm.addEventListener('submit', this.deleteCurrentDraft);
      }
    }
  }

  componentWillUnmount() {
    if (this.props.active && this.enclosingForm) {
      this.enclosingForm.removeEventListener('submit', this.deleteCurrentDraft);
    }

    if (this.throttledSaveDraft) {
      this.throttledSaveDraft.cancel();
    }

    clearTimeout(this.hideDraftTimer);
  }

  componentDidUpdate(prevProps) {
    if (!this.props.active) return;
    if (!prevProps.saveValueChanges || !this.props.saveValueChanges) return;

    if (this.props.documentChanged(prevProps.value, this.props.value)) {
      this.throttledSaveDraft(this.props.value);
    }
  }

  render() {
    if (
      this.props.active &&
      this.state.showRestoreText &&
      this.draftStorage.draftData
    ) {
      const draftDate = moment(this.draftStorage.draftData.savedAt);

      return (
        <div
          className='aha-editor-draft-metadata aha-editor-link-not-new-window'
          ref={this.ref}
        >
          Restored draft from {draftDate.format('MMMM Do YYYY, h:mm:ss a')} (
          <a href='#' onClick={this.undoDraftRestore}>
            undo
          </a>
          )
        </div>
      );
    } else if (this.props.active) {
      // Empty div, so we can navigate up to the enclosing form.
      return <div ref={this.ref} />;
    } else {
      return null;
    }
  }
}
